import type { VersionRef } from "../../hooks/useMaintenance.js";

/**
 * Eine Linie mit ihren Ständen.
 *
 * Die Hierarchie steckt seit jeher in `software_versions.parent_version_id`;
 * die Spalte „Linie" der Tabelle ist genau diese Kante, nur flach dargestellt.
 * Der Baum macht daraus die Form, in der man sie liest — ohne Schema, ohne
 * Migration, ohne zusätzliche Abfrage.
 */
export interface BaumKnoten {
  linie: VersionRef;
  kinder: VersionRef[];
}

/**
 * Versionen natürlich sortieren: „EHP8 SP 2" gehört vor „EHP8 SP 10".
 *
 * Ohne `numeric` sortiert der Zeichenvergleich stellenweise, und die zehnte
 * Version stünde zwischen der ersten und der zweiten. In einer Liste, deren
 * ganzer Zweck die Reihenfolge der Stände ist, wäre das der auffälligste
 * denkbare Fehler.
 */
function nachLabel(a: VersionRef, b: VersionRef): number {
  return a.label.localeCompare(b.label, "de", { numeric: true });
}

/**
 * Baut aus einer flachen Versionsliste die Baumform.
 *
 * **Es geht nichts verloren.** Eine Version, deren Linie nicht in der Liste
 * steht — theoretisch unmöglich, weil Linie und Stand immer zum selben Produkt
 * gehören, praktisch aber die Sorte Annahme, die irgendwann bricht —, wird als
 * eigene Wurzel behandelt statt stillschweigend verschluckt. Eine Ansicht, die
 * Zeilen verschweigt, ist schlimmer als eine, die sie unschön zeigt.
 */
export function baueBaum(versionen: VersionRef[]): BaumKnoten[] {
  const bekannt = new Set(versionen.map((v) => v.id));
  const wurzeln = versionen.filter(
    (v) => v.parentVersionId === null || !bekannt.has(v.parentVersionId),
  );

  return wurzeln.sort(nachLabel).map((linie) => ({
    linie,
    kinder: versionen
      .filter((v) => v.parentVersionId === linie.id)
      .sort(nachLabel),
  }));
}

/**
 * Der Baum als flache, geordnete Liste — mit der Tiefe je Zeile.
 *
 * **Warum flach und nicht verschachtelt.** Die erste Fassung rendelte den Baum
 * als eigene Ansicht mit eigenen Zeilen — und war damit nur lesbar: kein
 * Auswählen, kein Bearbeiten, kein Verschieben. Zwei Darstellungen bedeuten
 * zwei Codepfade, und die zweite kann weniger. Richtig ist dieselbe Tabelle in
 * anderer Reihenfolge: Die Hierarchie zeigt die Einrückung, alles Übrige
 * bleibt, wie es ist (Rückmeldung Hendrik, 2026-08-19).
 */
export function flacheHierarchie(
  versionen: VersionRef[],
): Array<{ version: VersionRef; tiefe: number }> {
  return baueBaum(versionen).flatMap((knoten) => [
    { version: knoten.linie, tiefe: 0 },
    ...knoten.kinder.map((k) => ({ version: k, tiefe: 1 })),
  ]);
}
